/**
 * Entry intent — what brought the user to onboarding.
 *
 * Set before the tutorial gate; used by TutorialScreen to pick the slide set
 * and by post-auth navigation to land users on the right screen.
 *
 * Persists across the install/sign-up gap (AsyncStorage on native, localStorage on web).
 * Cleared once the user has landed at their post-onboarding destination.
 */

export const ENTRY_INTENT_KIND = {
  COLD: 'cold',
  ORG: 'org',
  SQUAD_INVITE: 'squad-invite',
  GUEST: 'guest',
} as const;

export type EntryIntentKind =
  (typeof ENTRY_INTENT_KIND)[keyof typeof ENTRY_INTENT_KIND];

/**
 * `competitionId` says WHICH competition the squad belongs to. Optional: intents written
 * before competition-aware invites (and any mid-flight right now) have none and are World
 * Cup joins, so they must keep working across the deploy.
 */
export type EntryIntent =
  | { kind: 'cold' }
  | { kind: 'org'; orgSlug: string; inviteCode?: string; groupId?: string; competitionId?: string }
  | { kind: 'squad-invite'; inviteCode: string; groupId?: string; competitionId?: string }
  | { kind: 'guest' };

/** True when the intent's natural destination is a specific World Cup group page. */
export function intentHasGroupDestination(
  intent: EntryIntent | null
): intent is EntryIntent & { groupId: string } {
  if (!intent) return false;
  return (intent.kind === 'org' || intent.kind === 'squad-invite')
    && typeof (intent as { groupId?: unknown }).groupId === 'string'
    && (intent as { groupId: string }).groupId.length > 0;
}

